/**
 * Link page - captures username + server, kicks off the RCON-whisper flow,
 * collects the code, persists the resulting refresh token. Mirrors the
 * Windows client's LinkWindow but compressed into a single screen with a
 * stage flag.
 */

import { fetchServers, linkStart, linkRedeem, ApiError } from '../api.ts'
import {
  setRefreshToken,
  setLinkedAgid,
  setServerId,
  getServerId,
} from '../auth.ts'
import { el, mount } from '../ui/dom.ts'

type Stage = 'entry' | 'awaiting-code' | 'done'

export interface LinkPageOptions {
  readonly root: HTMLElement
  readonly onLinked: () => void
}

interface State {
  stage: Stage
  servers: ReadonlyArray<{ id: string; label: string }>
  serverId: string
  username: string
  code: string
  linkSessionId: string
  status: string
  statusKind: '' | 'ok' | 'error'
  busy: boolean
}

export function renderLinkPage(opts: LinkPageOptions): void {
  const state: State = {
    stage: 'entry',
    servers: [],
    serverId: getServerId() ?? '',
    username: '',
    code: '',
    linkSessionId: '',
    status: '',
    statusKind: '',
    busy: false,
  }

  // Closure captures `handlers` lexically; the body only reads it at call
  // time, so as long as we initialise `handlers` BEFORE the first rerender()
  // call we don't hit a TDZ. Calling rerender() inline above this line is a
  // ReferenceError - the const binding isn't initialised yet.
  const rerender = (): void => mount(opts.root, view(state, handlers))

  const handlers = {
    set: <K extends keyof State>(key: K, value: State[K]): void => {
      state[key] = value
      // iOS Safari fix: NEVER rerender on text-input changes. Calling
      // mount(opts.root, ...) tears down and recreates the focused <input>,
      // which iOS interprets as "input gone" and dismisses the keyboard
      // mid-typing. The <input> tracks its own value via the DOM; we just
      // need state[key] to be readable when Submit fires. For non-text
      // fields (serverId from <select>, etc.) we still rerender because
      // the change is visible elsewhere in the UI.
      if (key === 'username' || key === 'code') return
      rerender()
    },
    sendCode: async (): Promise<void> => {
      if (state.serverId === '' || state.username.trim() === '') {
        state.status = 'Pick a server and enter your in-game username.'
        state.statusKind = 'error'
        rerender()
        return
      }
      state.busy = true
      state.status = 'Asking the bridge to whisper a code in-game…'
      state.statusKind = ''
      rerender()
      try {
        const res = await linkStart(state.username.trim(), state.serverId)
        state.linkSessionId = res.linkSessionId
        state.stage = 'awaiting-code'
        state.status = `Check in-game chat - the bridge just whispered an AAVOICE-XXXX code to ${state.username.trim()}.`
        state.statusKind = 'ok'
      } catch (err) {
        state.status = errorToMessage(err, 'start')
        state.statusKind = 'error'
      } finally {
        state.busy = false
        rerender()
      }
    },
    submitCode: async (): Promise<void> => {
      if (state.code.trim().length === 0) {
        state.status = 'Enter the code from in-game chat.'
        state.statusKind = 'error'
        rerender()
        return
      }
      state.busy = true
      state.status = 'Redeeming code…'
      state.statusKind = ''
      rerender()
      try {
        const res = await linkRedeem(state.linkSessionId, state.code.trim())
        setRefreshToken(res.refreshToken)
        setLinkedAgid(res.agid)
        setServerId(state.serverId)
        state.stage = 'done'
        state.status = 'Linked! Loading voice…'
        state.statusKind = 'ok'
        rerender()
        // Small pause so the user can read the success message before the
        // voice page mounts and the link page DOM is replaced.
        setTimeout(() => opts.onLinked(), 600)
      } catch (err) {
        state.status = errorToMessage(err, 'redeem')
        state.statusKind = 'error'
        state.busy = false
        rerender()
      }
    },
    back: (): void => {
      state.stage = 'entry'
      state.code = ''
      state.status = ''
      state.statusKind = ''
      rerender()
    },
  }

  // First paint - safe to invoke rerender now that handlers is bound.
  rerender()

  // Server list comes from the bridge async; render the page immediately
  // with a "loading…" placeholder and patch the servers in when they land.
  void (async (): Promise<void> => {
    try {
      const res = await fetchServers()
      state.servers = res.servers
      if (state.serverId === '' && res.servers.length > 0) {
        state.serverId = res.servers[0]!.id
      }
      rerender()
    } catch (err) {
      state.status = errorToMessage(err, 'servers')
      state.statusKind = 'error'
      rerender()
    }
  })()
}

function view(state: State, h: {
  set: <K extends keyof State>(key: K, value: State[K]) => void
  sendCode: () => Promise<void>
  submitCode: () => Promise<void>
  back: () => void
}): HTMLElement {
  if (state.stage === 'entry') return entryView(state, h)
  if (state.stage === 'awaiting-code') return codeView(state, h)
  return doneView(state)
}

function entryView(state: State, h: {
  set: <K extends keyof State>(key: K, value: State[K]) => void
  sendCode: () => Promise<void>
}): HTMLElement {
  const serverSelect = el('select', {
    onchange: (e: Event) => h.set('serverId', (e.target as HTMLSelectElement).value),
  })
  if (state.servers.length === 0) {
    serverSelect.appendChild(el('option', { value: '' }, 'Loading servers…'))
    serverSelect.disabled = true
  } else {
    for (const s of state.servers) {
      const opt = el('option', { value: s.id }, s.label)
      if (s.id === state.serverId) opt.selected = true
      serverSelect.appendChild(opt)
    }
  }

  const usernameInput = el('input', {
    type: 'text',
    placeholder: 'Your in-game name',
    autocomplete: 'off',
    autocapitalize: 'off',
    autocorrect: 'off',
    spellcheck: false,
    value: state.username,
    // data-focus-key lets mount() restore focus + selection across any
    // rerender that happens while the user is typing, keeping the iOS
    // Safari soft keyboard up.
    'data-focus-key': 'username',
    oninput: (e: Event) => h.set('username', (e.target as HTMLInputElement).value),
  })

  const sendBtn = el(
    'button',
    {
      class: 'btn btn-primary btn-block',
      disabled: state.busy,
      onclick: () => { void h.sendCode() },
    },
    state.busy ? 'Sending…' : 'Send code',
  )

  return el('div', { class: 'page' }, [
    el('h1', undefined, 'Link your character'),
    el('p', { class: 'lede' }, 'Browser edition for mobile testers and anyone without the native app.'),
    el('div', { class: 'card' }, [
      el('label', undefined, 'Server'),
      serverSelect,
      el('label', undefined, 'In-game username'),
      usernameInput,
      el('div', { style: 'margin-top:18px' }, [sendBtn]),
      statusEl(state),
    ]),
    el('p', { class: 'small', style: 'margin-top:14px;text-align:center' },
      "You'll need to be logged into Path of Titans so the bridge can whisper you a code."),
  ])
}

function codeView(state: State, h: {
  set: <K extends keyof State>(key: K, value: State[K]) => void
  submitCode: () => Promise<void>
  back: () => void
}): HTMLElement {
  const codeInput = el('input', {
    type: 'text',
    placeholder: 'AAVOICE-XXXX',
    autocomplete: 'one-time-code',
    autocapitalize: 'characters',
    autocorrect: 'off',
    spellcheck: false,
    value: state.code,
    // See `data-focus-key` rationale on the username input.
    'data-focus-key': 'code',
    oninput: (e: Event) => h.set('code', (e.target as HTMLInputElement).value),
  })

  return el('div', { class: 'page' }, [
    el('h1', undefined, 'Enter the code'),
    el('p', { class: 'lede' }, `Look in your in-game chat for the whisper to ${state.username || 'your character'}. Type the code below within 5 minutes.`),
    el('div', { class: 'card' }, [
      el('label', undefined, 'Code'),
      codeInput,
      el('div', { style: 'display:flex;gap:8px;margin-top:18px' }, [
        el('button', { class: 'btn btn-primary', style: 'flex:1', disabled: state.busy, onclick: () => { void h.submitCode() } },
          state.busy ? 'Submitting…' : 'Submit'),
        el('button', { class: 'btn', onclick: () => h.back(), disabled: state.busy }, 'Back'),
      ]),
      statusEl(state),
    ]),
  ])
}

function doneView(state: State): HTMLElement {
  return el('div', { class: 'page' }, [
    el('h1', undefined, 'Linked!'),
    el('div', { class: 'card' }, [statusEl(state)]),
  ])
}

function statusEl(state: State): HTMLElement | null {
  if (state.status.length === 0) return null
  return el('div', { class: `status ${state.statusKind}` }, state.status)
}

function errorToMessage(err: unknown, phase: 'start' | 'redeem' | 'servers'): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'user_not_online':
        return "We can't see that username on the server you picked from the dropdown above. Make sure (1) you're actually logged into Path of Titans right now on THAT exact server, and (2) the username is spelled exactly the same as your PoT in-game name. If you're on a different PoT server, change the selection above."
      case 'unknown_server':
        return 'The selected server isn\'t reachable from this bridge. Try a different server.'
      case 'invalid_request':
        return 'Something about the request was rejected. Double-check the inputs and try again.'
      case 'http_429':
      case 'rate_limited':
        return 'Slow down - too many recent attempts. Wait a minute and try again.'
      default:
        return `Bridge error (${err.code}). Try again, or come back in a minute.`
    }
  }
  return phase === 'servers'
    ? "Couldn't reach the bridge to load the server list."
    : 'Network error - check your connection and try again.'
}
